import React from 'react';
import { Container, Typography, Paper, Box, Grid, Button, Card, CardContent, CircularProgress, Divider, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import NightlifeIcon from '@mui/icons-material/Nightlife';
import CelebrationIcon from '@mui/icons-material/Celebration';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EventIcon from '@mui/icons-material/Event';
import TableBarIcon from '@mui/icons-material/TableBar';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { useTranslation } from 'react-i18next';

const ClubEntryPage: React.FC = () => {
  const { t } = useTranslation('salesSystem');
  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      overflowX: 'hidden'
    }}>
      <Box 
        sx={{ 
          height: 'calc(100vh - 80px)', 
          overflowY: 'auto', 
          overflowX: 'hidden',
          width: '100%',
          paddingRight: '10px',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            marginLeft: '10px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
            '&:hover': {
              background: '#555'
            }
          }
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ pb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <NightlifeIcon sx={{ fontSize: 40, mr: 2, color: 'secondary.main' }} />
              <Typography variant="h4" component="h1">
                {t('clubEntryPage.title')}
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
              {t('clubEntryPage.subtitle')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, mb: 4, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    {t('clubEntryPage.features.title')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <QrCodeScannerIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('clubEntryPage.features.items.qrTicketScan.title')} 
                        secondary={t('clubEntryPage.features.items.qrTicketScan.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CreditCardIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('clubEntryPage.features.items.payments.title')} 
                        secondary={t('clubEntryPage.features.items.payments.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TableBarIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('clubEntryPage.features.items.tableManagement.title')} 
                        secondary={t('clubEntryPage.features.items.tableManagement.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <EventIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('clubEntryPage.features.items.eventManagement.title')} 
                        secondary={t('clubEntryPage.features.items.eventManagement.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PersonAddIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('clubEntryPage.features.items.guestRegistration.title')} 
                        secondary={t('clubEntryPage.features.items.guestRegistration.description')}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, mb: 4, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    {t('clubEntryPage.developmentStatus.title')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ my: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Card sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="h6">{t('clubEntryPage.developmentStatus.analysis.title')}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                              <CircularProgress variant="determinate" value={90} color="success" />
                              <Typography variant="body2" sx={{ ml: 2 }}>{t('clubEntryPage.developmentStatus.analysis.progress')}</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12}>
                        <Card sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="h6">{t('clubEntryPage.developmentStatus.design.title')}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                              <CircularProgress variant="determinate" value={40} color="primary" />
                              <Typography variant="body2" sx={{ ml: 2 }}>{t('clubEntryPage.developmentStatus.design.progress')}</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6">{t('clubEntryPage.developmentStatus.development.title')}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                              <CircularProgress variant="determinate" value={15} color="warning" />
                              <Typography variant="body2" sx={{ ml: 2 }}>{t('clubEntryPage.developmentStatus.development.progress')}</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: 'rgba(156, 39, 176, 0.04)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CelebrationIcon sx={{ fontSize: 30, mr: 2, color: 'secondary.main' }} />
                <Typography variant="h6">
                  {t('clubEntryPage.comingSoon.title')}
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                {t('clubEntryPage.comingSoon.description')}
              </Typography>
            </Paper>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    {t('clubEntryPage.faceRecognition.title')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="body2" paragraph color="textSecondary">
                    {t('clubEntryPage.faceRecognition.description')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
                    <GroupsIcon sx={{ fontSize: 30, mr: 2, color: 'secondary.main' }} />
                    <Typography variant="body1">
                      {t('clubEntryPage.faceRecognition.vipServiceTitle')}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" paragraph color="textSecondary" sx={{ mt: 1 }}>
                    {t('clubEntryPage.faceRecognition.vipServiceDescription')}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    {t('clubEntryPage.eventPerformance.title')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="body2" paragraph color="textSecondary">
                    {t('clubEntryPage.eventPerformance.description')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
                    <MusicNoteIcon sx={{ fontSize: 30, mr: 2, color: 'secondary.main' }} />
                    <Typography variant="body1">
                      {t('clubEntryPage.eventPerformance.djProgramTitle')}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" paragraph color="textSecondary" sx={{ mt: 1 }}>
                    {t('clubEntryPage.eventPerformance.djProgramDescription')}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 4, pb: 4 }}>
              <Button variant="contained" color="secondary" disabled>
                {t('clubEntryPage.cta')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ClubEntryPage; 